<template>
    <div>
        <!-- Card principale -->
        <div class="card border-0 shadow-sm mb-4" style="border-radius: 12px;">
            <div class="card-header bg-white border-0 pt-4 px-4 pb-0 d-flex justify-content-between align-items-center">
                <h2 class="h5 font-weight-bold mb-0 text-dark">
                    <i class="far fa-map-marked-alt mr-2 text-primary"></i> Zones de stockage
                </h2>
                <div class="d-flex align-items-center" v-if="canManage || canTransfer">
                    <button v-if="canTransfer && !showTransferForm" class="ix-btn blue btn-sm mr-2" @click="openTransferForm">
                        <i class="far fa-exchange-alt mr-1"></i> {{ emplacements.length > 0 ? 'Transférer du stock' : 'Affecter à une zone de stockage' }}
                    </button>
                </div>
            </div>

            <div class="card-body p-4">
                <!-- Formulaire de transfert interne entre emplacements -->
                <div v-if="showTransferForm" class="p-4 mb-4 border-0 shadow-sm bg-light-blue" style="border-radius: 12px; background-color: #f8fafc; border: 1px solid #e2e8f0 !important;">
                    <div class="d-flex justify-content-between align-items-center mb-3">
                        <h3 class="h6 font-weight-bold text-dark mb-0 d-flex align-items-center">
                            <i class="far fa-exchange-alt text-primary mr-2"></i> Transférer du stock <span class="text-muted font-weight-normal small ml-2">(Mouvement Interne)</span>
                        </h3>
                    </div>
                    
                    <div class="row">
                        <!-- Source (seulement si on a des emplacements) -->
                        <div class="col-md-5 form-group" v-if="emplacements.length > 0">
                            <label class="required font-weight-bold text-secondary small">Zone d'origine</label>
                            <div class="emp-dropdown" v-click-outside="closeSourceDropdown">
                                <!-- Trigger -->
                                <div class="emp-dropdown-trigger" @click="sourceDropdownOpen = !sourceDropdownOpen">
                                    <template v-if="!transferForm.source_id">
                                        <i class="far fa-hand-pointer text-muted"></i>
                                        <span class="text-muted">Choisir l'origine...</span>
                                    </template>
                                    <template v-else-if="transferForm.source_id === 'unassigned'">
                                        <i class="far fa-boxes-stacked text-warning"></i>
                                        <span class="emp-source-label">Stock non affecté</span>
                                        <span class="emp-source-qty text-warning ml-auto">{{ formatQuantity(unassignedStock) }} {{ unit }}</span>
                                    </template>
                                    <template v-else>
                                        <i class="far fa-map-marker-alt text-primary"></i>
                                        <span class="emp-source-label">{{ selectedSourceEmp ? selectedSourceEmp.name : '' }}</span>
                                        <span class="emp-source-qty text-primary ml-auto">{{ selectedSourceEmp ? formatQuantity(selectedSourceEmp.quantity) : '' }} {{ unit }}</span>
                                    </template>
                                    <i class="far fa-chevron-down emp-dropdown-caret" :class="{ open: sourceDropdownOpen }"></i>
                                </div>
                                <!-- Options -->
                                <div class="emp-dropdown-menu" v-if="sourceDropdownOpen">
                                    <div v-if="unassignedStock > 0"
                                         class="emp-source-item emp-source-unassigned"
                                         :class="{ active: transferForm.source_id === 'unassigned' }"
                                         @click="selectSource('unassigned')">
                                        <i class="far fa-boxes-stacked text-warning"></i>
                                        <div class="emp-source-info">
                                            <span class="emp-source-label">Stock non affecté</span>
                                        </div>
                                        <span class="emp-source-qty text-warning">{{ formatQuantity(unassignedStock) }} {{ unit }}</span>
                                    </div>
                                    <div v-for="emp in emplacements" :key="emp.id"
                                         class="emp-source-item"
                                         :class="{ active: transferForm.source_id === emp.id }"
                                         @click="selectSource(emp.id)">
                                        <i class="far fa-map-marker-alt text-primary"></i>
                                        <div class="emp-source-info">
                                            <span class="emp-source-label">{{ emp.name }}</span>
                                            <span class="emp-source-path text-muted">{{ emp.full_path }}</span>
                                        </div>
                                        <span class="emp-source-qty text-primary">{{ formatQuantity(emp.quantity) }} {{ unit }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Destination -->
                        <div class="col-md-5 form-group">
                            <label class="required font-weight-bold text-secondary small">Zone de destination</label>
                            <emplacement-tree-selector 
                                v-if="emplacements.length === 0 || transferForm.source_id"
                                @change="onTransferDestinationSelect" 
                                :exclude-id="transferForm.source_id === 'unassigned' ? null : transferForm.source_id"
                                placeholder="Rechercher la zone de destination...">
                            </emplacement-tree-selector>
                            <div v-else class="form-control bg-light text-muted" style="min-height: 38px; display: flex; align-items: center; border-radius: 8px; font-size: 13.5px; cursor: not-allowed;">
                                Sélectionnez d'abord une zone d'origine...
                            </div>
                        </div>
                        
                        <!-- Quantité -->
                        <div class="col-md-2 form-group">
                            <label class="required font-weight-bold text-secondary small">Quantité</label>
                            <div class="input-group input-group-sleek">
                                <input type="number" 
                                       class="form-control" 
                                       v-model.number="transferForm.quantity" 
                                       min="0.01" 
                                       :max="maxTransferQuantity" 
                                       step="any" 
                                       placeholder="0"
                                       :disabled="hasSerialNumbers">
                                <div class="input-group-append">
                                    <span class="input-group-text bg-white border-left-0">{{ unit }}</span>
                                </div>
                            </div>
                            <small class="text-muted d-block mt-1">Stock disponible : {{ formatQuantity(maxTransferQuantity) }} {{ unit }}</small>
                        </div>
                    </div>

                    <!-- Sélection des numéros de série (si l'article gère les SN) -->
                    <div v-if="hasSerialNumbers && showTransferForm" class="mt-3">
                        <label class="required font-weight-bold text-secondary small">Numéros de série à transférer</label>
                        <div v-if="snLoading" class="text-muted small py-2">
                            <i class="far fa-circle-notch fa-spin mr-1"></i> Chargement des numéros de série...
                        </div>
                        <div v-else-if="availableSerialNumbers.length === 0" class="text-muted small py-2">
                            <i class="far fa-info-circle mr-1"></i> Aucun numéro de série disponible pour cette zone.
                        </div>
                        <div v-else>
                            <!-- Recherche + actions groupées -->
                            <div class="d-flex align-items-center mb-2" style="gap: 8px;">
                                <div class="input-group input-group-sleek flex-grow-1" style="height: 34px;">
                                    <input type="text" 
                                           class="form-control sn-search-input" 
                                           v-model="snSearch" 
                                           placeholder="Rechercher un numéro de série..."
                                           style="font-size: 13px;">
                                </div>
                                <button type="button" class="btn btn-sm btn-outline-primary sn-btn-bulk" @click="selectAllFiltered" style="white-space: nowrap;">
                                    Tout sélectionner
                                </button>
                                <button type="button" class="btn btn-sm btn-outline-secondary sn-btn-bulk" @click="clearSelection" style="white-space: nowrap;">
                                    Effacer
                                </button>
                            </div>

                            <!-- Chips des SN sélectionnés -->
                            <div v-if="transferForm.serial_numbers.length > 0" class="sn-chips-container mb-2">
                                <span v-for="sn in transferForm.serial_numbers" :key="sn" class="sn-chip">
                                    {{ sn }}
                                    <i class="far fa-times ml-1 sn-chip-remove" @click="toggleSerialNumber(sn)"></i>
                                </span>
                            </div>

                            <!-- Liste filtrée -->
                            <div class="sn-select-container">
                                <div v-for="sn in filteredSerialNumbers" :key="sn.id" class="sn-select-item" :class="{ selected: transferForm.serial_numbers.includes(sn.serial_number) }" @click="toggleSerialNumber(sn.serial_number)">
                                    <i class="far" :class="transferForm.serial_numbers.includes(sn.serial_number) ? 'fa-check-square text-primary' : 'fa-square text-muted'"></i>
                                    <span class="sn-value">{{ sn.serial_number }}</span>
                                </div>
                                <div v-if="filteredSerialNumbers.length === 0" class="text-muted small text-center py-2">
                                    Aucun numéro de série trouvé pour "{{ snSearch }}".
                                </div>
                            </div>
                            <small class="text-muted d-block mt-2">{{ transferForm.serial_numbers.length }} / {{ availableSerialNumbers.length }} numéro(s) sélectionné(s)</small>
                        </div>
                    </div>

                    <!-- Actions -->
                    <div class="d-flex justify-content-end align-items-center mt-2 pt-2 border-top" style="border-top-color: #f1f5f9 !important;">
                        <button class="btn btn-link text-secondary font-weight-medium mr-3 px-3 py-2 btn-sleek-cancel" type="button" @click="closeTransferForm" :disabled="submitting" style="font-size: 13.5px; text-decoration: none;">
                            Annuler
                        </button>
                        <button class="ix-btn blue px-4 py-2" @click="submitTransfer" :disabled="submitting || (emplacements.length > 0 && !transferForm.source_id) || !transferForm.destination_id || transferForm.quantity <= 0 || transferForm.quantity > maxTransferQuantity || (hasSerialNumbers && transferForm.serial_numbers.length === 0)" style="height: 38px; border-radius: 8px; font-weight: 600; font-size: 13.5px;">
                            <span v-if="submitting"><i class="far fa-circle-notch fa-spin mr-1"></i></span>
                            <span v-else>Valider le transfert</span>
                        </button>
                    </div>
                </div>

                <!-- Indicateur de chargement -->
                <div v-if="loading" class="text-center py-5">
                    <i class="far fa-circle-notch fa-spin text-primary mr-2" style="font-size: 32px;"></i>
                    <p class="mt-2 text-muted small">Chargement des zones de stockage...</p>
                </div>

                <!-- Liste d'emplacements vide -->
                <div v-else-if="emplacements.length === 0" class="text-center py-5 text-secondary">
                    <i class="far fa-map-marker-slash text-muted mb-3" style="font-size: 48px;"></i>
                    <h3 class="h5 font-weight-bold text-dark mb-1">Aucune zone de stockage</h3>
                    <p class="small mb-0" style="max-width: 350px; margin: 0 auto;">Cet article n'est actuellement stocké dans aucune zone de stockage.</p>
                </div>

                <!-- Arborescence des emplacements -->
                <div v-else class="emp-tree-container">
                    <div class="emp-tree-header d-flex align-items-center justify-content-between px-3 py-2 mb-0" style="background: #f8fafc; border-radius: 8px 8px 0 0; border: 1px solid #e2e8f0; border-bottom: none;">
                        <span class="font-weight-bold text-secondary small text-uppercase" style="letter-spacing: 0.5px;">Zone de stockage</span>
                        <span class="font-weight-bold text-secondary small text-uppercase" style="letter-spacing: 0.5px;">Quantité</span>
                    </div>
                    <div class="emp-tree-body" style="border: 1px solid #e2e8f0; border-radius: 0 0 8px 8px; overflow: hidden;">
                        <emp-tree-node
                            v-for="node in emplacementTree"
                            :key="node.id"
                            :node="node"
                            :unit="unit"
                            :level="0">
                        </emp-tree-node>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import EmplacementSelector from './EmplacementSelector.vue';
import EmplacementTreeSelector from './EmplacementTreeSelector.vue';
import EmpTreeNode from './EmpTreeNode.vue';

export default {
    name: 'article-emplacements-manager',
    components: {
        EmplacementSelector,
        EmplacementTreeSelector,
        EmpTreeNode
    },
    props: {
        articleId: {
            type: Number,
            required: true
        },
        unit: {
            type: String,
            default: 'unité(s)'
        },
        canManage: {
            type: Boolean,
            default: false
        },
        canTransfer: {
            type: Boolean,
            default: false
        },
        articleStock: {
            type: Number,
            default: 0
        },
        hasSerialNumbers: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            emplacements: [],
            loading: true,
            submitting: false,
            
            // État du formulaire de transfert interne entre emplacements
            showTransferForm: false,
            sourceDropdownOpen: false,
            transferForm: {
                source_id: null,
                destination_id: null,
                quantity: 0,
                serial_numbers: []
            },
            availableSerialNumbers: [],
            snLoading: false,
            snSearch: '',

        };
    },
    computed: {
        assignedStock() {
            return this.emplacements.reduce((sum, emp) => sum + parseFloat(emp.quantity), 0);
        },
        unassignedStock() {
            return Math.max(0, parseFloat(this.articleStock) - this.assignedStock);
        },
        selectedSourceEmp() {
            if (!this.transferForm.source_id || this.transferForm.source_id === 'unassigned') return null;
            return this.emplacements.find(e => e.id === this.transferForm.source_id);
        },
        emplacementTree() {
            const map = {};
            const roots = [];
            this.emplacements.forEach(emp => {
                map[emp.id] = { ...emp, children: [] };
            });
            this.emplacements.forEach(emp => {
                if (emp.parent_id && map[emp.parent_id]) {
                    map[emp.parent_id].children.push(map[emp.id]);
                } else {
                    roots.push(map[emp.id]);
                }
            });
            return roots;
        },
        maxTransferQuantity() {
            if (this.emplacements.length === 0) {
                return parseFloat(this.articleStock);
            }
            if (!this.transferForm.source_id) return 0;
            if (this.transferForm.source_id === 'unassigned') {
                return this.unassignedStock;
            }
            const emp = this.emplacements.find(e => e.id === this.transferForm.source_id);
            return emp ? parseFloat(emp.quantity) : 0;
        },
        filteredSerialNumbers() {
            if (!this.snSearch) return this.availableSerialNumbers;
            const term = this.snSearch.toLowerCase();
            return this.availableSerialNumbers.filter(sn => sn.serial_number.toLowerCase().includes(term));
        }
    },
    methods: {
        formatQuantity(q) {
            if (q === null || q === undefined) return 0;
            return parseFloat(q);
        },

        fetchEmplacements() {
            this.loading = true;
            fetch(`/stocks/articles/${this.articleId}/emplacements`, {
                method: 'GET',
                ...this.getHttpJsonRequestHeaders()
            })
            .then(res => {
                if (!res.ok) throw new Error("Impossible de charger les emplacements.");
                return res.json();
            })
            .then(data => {
                if (data.success) {
                    this.emplacements = data.emplacements;
                }
                this.loading = false;
            })
            .catch(err => {
                console.error(err);
                this.loading = false;
            });
        },

        // --- Transfert Interne ---
        openTransferForm() {
            this.showTransferForm = true;
            this.transferForm.source_id = null;
            this.transferForm.destination_id = null;
            this.transferForm.quantity = 0;
            this.transferForm.serial_numbers = [];
            this.availableSerialNumbers = [];
            this.snSearch = '';
        },

        selectSource(sourceId) {
            this.transferForm.source_id = sourceId;
            this.transferForm.destination_id = null;
            this.transferForm.quantity = 0;
            this.transferForm.serial_numbers = [];
            this.snSearch = '';
            this.sourceDropdownOpen = false;
            if (this.hasSerialNumbers) {
                this.fetchSerialNumbers();
            }
        },

        fetchSerialNumbers() {
            this.snLoading = true;
            this.availableSerialNumbers = [];
            const empId = this.transferForm.source_id === 'unassigned' ? null : this.transferForm.source_id;
            let url = `/api/articles/${this.articleId}/serial-numbers`;
            if (empId) url += `?emplacement_id=${empId}`;
            fetch(url, { ...this.getHttpJsonRequestHeaders() })
                .then(res => res.json())
                .then(data => {
                    this.availableSerialNumbers = data;
                    this.snLoading = false;
                })
                .catch(() => { this.snLoading = false; });
        },

        toggleSerialNumber(sn) {
            const idx = this.transferForm.serial_numbers.indexOf(sn);
            if (idx > -1) {
                this.transferForm.serial_numbers.splice(idx, 1);
            } else {
                this.transferForm.serial_numbers.push(sn);
            }
            this.transferForm.quantity = this.transferForm.serial_numbers.length;
        },

        selectAllFiltered() {
            this.filteredSerialNumbers.forEach(sn => {
                if (!this.transferForm.serial_numbers.includes(sn.serial_number)) {
                    this.transferForm.serial_numbers.push(sn.serial_number);
                }
            });
            this.transferForm.quantity = this.transferForm.serial_numbers.length;
        },

        clearSelection() {
            this.transferForm.serial_numbers = [];
            this.transferForm.quantity = 0;
        },

        closeSourceDropdown() {
            this.sourceDropdownOpen = false;
        },

        closeTransferForm() {
            this.showTransferForm = false;
        },

        onTransferDestinationSelect(emplacement) {
            this.transferForm.destination_id = emplacement ? emplacement.id : null;
        },

        submitTransfer() {
            if (this.emplacements.length > 0 && !this.transferForm.source_id) return;
            if (!this.transferForm.destination_id || this.transferForm.quantity <= 0) return;

            if (this.transferForm.quantity > this.maxTransferQuantity) {
                this.$swal({
                    title: 'Erreur',
                    text: this.transferForm.source_id === 'unassigned' || this.emplacements.length === 0
                        ? 'La quantité ne peut pas dépasser le stock non affecté de l\'article.'
                        : 'La quantité à transférer dépasse la quantité disponible à l\'emplacement d\'origine.',
                    icon: 'error'
                });
                return;
            }

            this.submitting = true;

            const csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute('content');

            var payload = {
                source_id: this.transferForm.source_id === 'unassigned' ? null : this.transferForm.source_id,
                destination_id: this.transferForm.destination_id,
                quantity: this.hasSerialNumbers ? this.transferForm.serial_numbers.length : this.transferForm.quantity,
                serial_numbers: this.hasSerialNumbers ? this.transferForm.serial_numbers : []
            };

            fetch(`/stocks/articles/${this.articleId}/transfer`, {
                method: 'POST',
                body: JSON.stringify(payload),
                ...this.getHttpJsonRequestHeaders(),
                headers: {
                    ...this.getHttpJsonRequestHeaders().headers,
                    'X-CSRF-TOKEN': csrfToken
                }
            })
            .then(res => {
                if (!res.ok) throw new Error("Erreur lors du transfert.");
                return res.json();
            })
            .then(data => {
                if (data.success) {
                    if (window.toastr) {
                        window.toastr.success(data.message);
                    }
                    this.showTransferForm = false;
                    this.fetchEmplacements();
                }
                this.submitting = false;
            })
            .catch(err => {
                this.submitting = false;
                this.$swal({
                    title: 'Erreur',
                    text: err.message,
                    icon: 'error'
                });
            });
        }
    },
    directives: {
        'click-outside': {
            bind(el, binding) {
                el._clickOutside = function(e) {
                    if (!(el === e.target || el.contains(e.target))) {
                        binding.value(e);
                    }
                };
                document.addEventListener('click', el._clickOutside);
            },
            unbind(el) {
                document.removeEventListener('click', el._clickOutside);
            }
        }
    },
    mounted() {
        this.fetchEmplacements();
    }
};
</script>

<style scoped>
.form-group label.required::after {
    content: " *";
    color: #ef4444;
    font-weight: bold;
}
.form-control:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
}

/* Styles épurés de formulaires hauts de gamme */
.custom-select-sleek, 
.input-group-sleek .form-control {
    height: 38px !important;
    border: 1px solid #cbd5e1 !important;
    border-radius: 8px !important;
    font-size: 13.5px !important;
    color: #1e293b !important;
    background-color: #ffffff !important;
    transition: all 0.15s ease-in-out !important;
}

.custom-select-sleek:focus, 
.input-group-sleek:focus-within {
    border-color: #3b82f6 !important;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15) !important;
}

.input-group-sleek .input-group-text {
    border: 1px solid #cbd5e1 !important;
    border-left: none !important;
    border-top-right-radius: 8px !important;
    border-bottom-right-radius: 8px !important;
    color: #64748b !important;
    font-size: 13px !important;
    background-color: #f8fafc !important;
}

.input-group-sleek .form-control:disabled {
    background-color: #e9ecef !important;
    color: #6c757d !important;
    cursor: not-allowed !important;
    opacity: 0.65 !important;
}

.btn-sleek-cancel {
    color: #64748b !important;
    transition: all 0.15s ease-in-out !important;
}

.btn-sleek-cancel:hover {
    color: #1e293b !important;
    background-color: #f1f5f9 !important;
    border-radius: 8px !important;
}

/* Dropdown personnalisé de sélection d'origine */
.emp-dropdown {
    position: relative;
}

.emp-dropdown-trigger {
    display: flex;
    align-items: center;
    gap: 10px;
    height: 38px;
    padding: 0 14px;
    border: 1px solid #cbd5e1;
    border-radius: 8px;
    background: #fff;
    cursor: pointer;
    font-size: 13.5px;
    transition: all 0.15s ease;
}

.emp-dropdown-trigger:hover {
    border-color: #94a3b8;
}

.emp-dropdown-trigger:focus-within {
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
}

.emp-dropdown-caret {
    margin-left: auto;
    font-size: 12px;
    color: #94a3b8;
    transition: transform 0.2s ease;
}

.emp-dropdown-caret.open {
    transform: rotate(180deg);
}

.emp-dropdown-menu {
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    right: 0;
    max-height: 240px;
    overflow-y: auto;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    background: #fff;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    z-index: 10;
}

.emp-source-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 14px;
    cursor: pointer;
    border-bottom: 1px solid #f1f5f9;
    transition: all 0.12s ease;
    font-size: 13.5px;
}

.emp-source-item:last-child {
    border-bottom: none;
}

.emp-source-item:hover {
    background-color: #f8fafc;
}

.emp-source-item.active {
    background-color: #eff6ff;
    border-left: 3px solid #3b82f6;
    padding-left: 11px;
}

.emp-source-item > i {
    font-size: 16px;
    flex-shrink: 0;
}

.emp-source-info {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0;
}

.emp-source-label {
    font-weight: 600;
    color: #1e293b;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.emp-source-path {
    font-size: 11px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.emp-source-qty {
    font-weight: 700;
    font-size: 13px;
    flex-shrink: 0;
    margin-left: auto;
}

.emp-source-unassigned {
    background-color: #fffbeb;
}

.emp-source-unassigned:hover {
    background-color: #fef3c7;
}

.emp-source-unassigned.active {
    background-color: #fef3c7;
    border-left-color: #f59e0b;
}

.sn-select-container {
    max-height: 200px;
    overflow-y: auto;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 8px;
    background: #fff;
}

.sn-select-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 10px;
    cursor: pointer;
    border-radius: 6px;
    transition: all 0.12s ease;
    font-size: 13px;
}

.sn-select-item:hover {
    background-color: #f8fafc;
}

.sn-select-item.selected {
    background-color: #eff6ff;
}

.sn-select-item .sn-value {
    font-family: monospace;
    color: #1e293b;
}

.sn-search-input {
    border: 1px solid #cbd5e1 !important;
    border-radius: 8px !important;
    height: 34px !important;
    box-shadow: none !important;
    background-color: #ffffff !important;
}

.sn-search-input:focus {
    border-color: #3b82f6 !important;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15) !important;
}

.sn-btn-bulk {
    font-size: 12px;
    font-weight: 600;
    border-radius: 8px;
    padding: 4px 10px;
    height: 34px;
    line-height: 1.4;
}

.sn-chips-container {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    max-height: 80px;
    overflow-y: auto;
    padding: 4px 0;
}

.sn-chip {
    display: inline-flex;
    align-items: center;
    background-color: #eff6ff;
    border: 1px solid #bfdbfe;
    border-radius: 6px;
    padding: 3px 8px;
    font-size: 12px;
    font-family: monospace;
    color: #1e40af;
    white-space: nowrap;
}

.sn-chip-remove {
    cursor: pointer;
    opacity: 0.6;
    transition: opacity 0.15s ease;
}

.sn-chip-remove:hover {
    opacity: 1;
}
</style>
